import React, { useEffect, useRef, useState } from "react";
import { Button, Offcanvas, Form, Row, Col } from "react-bootstrap";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import Select from "react-select";
import {
  GetRequest,
  PostRequest,
  PutRequest,
} from "../../Utils/hooks/ApiHandler";
import useLocalStorageState from "use-local-storage-state";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { IconTrash } from "@tabler/icons-react";
import { customStyles } from "../../Utils/helpers/commonhelper";
import { baseURL } from "../../Utils/constants/constant";

const roundRobinTypeOptions = [
  { value: "roundrobin", label: "Round Robin" },
  { value: "custom_algorithm", label: "Custom" },
];
const CreateRoundRobinOffcanvas = ({
  show,
  setShowCanvas,
  setEditedId,
  editedId,
  fetchRoundRobin,
}) => {
  const navigate = useNavigate();
  const [customer, setCustomer] = useLocalStorageState("customer", {
    token: {},
  });
  const customerId = customer?.customer_details?.customer_id;
  const token = customer?.token?.authorization;
  const rf = customer?.token?.authentication;
  const headers = { authentication: rf, Authorization: token };
  const extras = { customer, setCustomer, rf, navigate };
  const [leadOwnerOptions, setLeadOwnerOptions] = useState([]);
  const {
    control,
    handleSubmit,
    watch,
    reset,
    register,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      name: "",
      description: "",
      type: { value: "roundrobin", label: "Round Robin" },
      owners_multi: [],
      custom_owners: [],
    },
  });
  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "custom_owners",
  });
  const selectedType = watch("type");
  const isCustom = selectedType?.value === "custom_algorithm";
  useEffect(() => {
    (async () => {
      const res = await GetRequest(
        `users/users/fetchUserAsOwner/${customerId}`,
        { headers },
        extras,
      );
      if (!res.err) {
        const options =
          res.data?.map((lead) => ({
            label: lead.full_name,
            value: String(lead.id),
          })) || [];
        setLeadOwnerOptions(options);
      }
    })();
  }, [customerId]);
  useEffect(() => {
    if (show && editedId) {
      (async () => {
        const res = await GetRequest(
          `${baseURL}workflow/fetchDistributionDetails_update/${customerId}/${editedId}`,
          { headers },
          extras,
        );
        if (res.status === "success" && res.data) {
          const rawData = Array.isArray(res.data) ? res.data[0] : res.data;
          let parsedUsers = [];
          try {
            parsedUsers =
              typeof rawData?.users === "string"
                ? JSON.parse(rawData.users)
                : rawData?.users || [];
          } catch (err) {
            parsedUsers = [];
          }
          const currentType =
            roundRobinTypeOptions.find((opt) => opt.value === rawData.type) ||
            roundRobinTypeOptions[0];
            const resetPayload = {
            name: rawData.workflow_name || "",
            description: rawData.description || "",
            type: currentType,
            owners_multi: [],
            custom_owners: [],
          };
          if (rawData.type === "roundrobin") {
            resetPayload.owners_multi = parsedUsers.map((u) => ({
              value: String(u.id),
              label: u.name,
            }));
          } else if (rawData.type === "custom_algorithm") {
            resetPayload.custom_owners = parsedUsers.map((u) => ({
              owner: { value: String(u.id), label: u.name },
              count: u.count || 1,
            }));
          }
          reset(resetPayload);
        }
      })();
    } else if (show && !editedId) {
      reset({
        name: "",
        description: "",
        type: { value: "roundrobin", label: "Round Robin" },
        owners_multi: [],
        custom_owners: [],
      });
    }
  }, [editedId, show, reset]);
  const handleClose = () => {
    reset();
    setShowCanvas(false);
    setEditedId(null);
  };
  const onSubmit = async (data) => {
    try {
      let payload = {
        user_login_id: customer?.customer_details?.id,
        customer_id: customerId,
        workflow_name: data.name,
        description: data.description,
        type: data.type?.value,
      };
      if (editedId) {
        payload.workflow_id = editedId;
      }
      if (data.type?.value === "roundrobin") {
        payload.users =
          data.owners_multi?.map((o) => ({ id: o.value, name: o.label })) || [];
      } else {
        payload.users = data.custom_owners?.map((row) => ({
          id: row.owner?.value,
          name: row.owner?.label,
          count: Number(row.count) || 1,
        }));
      }
      let res;
      if (!editedId) {
        res = await PostRequest(
          `${baseURL}workflow/insertDistributionWorkflow`,
          payload,
          false,
          { headers },
          extras,
        );
      } else {
        res = await PutRequest(
          `${baseURL}workflow/updateDistributionWorkflow`,
          payload,
          { headers },
          extras,
        );
      }
      if (res?.status === "success") {
        toast.success(
          editedId ? "Updated successfully!" : "Created successfully!",
        );
        fetchRoundRobin();
        handleClose();
      } else {
        toast.error(res?.message || "Something went wrong");
      }
    } catch (err) {
      toast.error("Failed to process request");
    }
  };
  return (
    <Offcanvas
      className="createContactOffcanvas"
      placement="end"
      backdrop="static"
      enforceFocus={true}
      show={show}
      onHide={handleClose}
      style={{ width: "60%" }}
    >
      <Offcanvas.Header
        closeButton
        className="border-bottom border-dashed pt-3"
      >
        <div className="d-flex align-items-center justify-content-between gap-2">
          <div className="pt-0 ps-4">
            <Offcanvas.Title
              style={{ color: "#0791ff" }}
              className="addEnquiryHead"
            >
              {editedId ? "Edit Round Robin" : "Create Round Robin"}
            </Offcanvas.Title>
            <p
                style={{
                  fontSize: "14px",
                  color: "#b26792",
                  lineHeight: 1.1,
                }}
                className="text-secondary text-nowrap mb-0"
            >
                {editedId ? "Update the round-robin settings to change how leads are assigned among team members." :"Configure how leads are distributed among selected team members in a round-robin manner."}
            </p>
          </div>
        </div>
      </Offcanvas.Header>
      <Offcanvas.Body className="pt-3">
        <form
          className="form-container p-3 w-100 pe-0 ps-0"
          onSubmit={handleSubmit(onSubmit)}
          style={{ display: "flex", flexDirection: "column", height: "100%" }}
        >
          <div style={{ flex: 1, overflowY: "auto", paddingBottom: "90px" }}>
            <div className="mb-3">
              <Row className="g-3">
                <Col xs={6} className="mb-3">
                  <Form.Group className=" d-flex flex-column focus-z-index position-relative me-2">
                    <Form.Label>
                      Workflow Name<span className="text-danger">*</span>
                    </Form.Label>
                    <input
                      className="form-control form-control-sm"
                      {...register("name", {
                        required: "Workflow name is required",
                      })}
                      placeholder="Enter workflow name"
                    />
                  </Form.Group>
                  {errors.name && (
                    <small className="text-danger">{errors.name.message}</small>
                  )}
                </Col>

                <Col xs={6}>
                  <Form.Group className="d-flex flex-column focus-z-index position-relative">
                    <Form.Label
                      className="fw-semibold mb-1"
                      style={{ fontSize: "13px", color: "#555" }}
                    >
                      Description<span className="text-danger">*</span>
                    </Form.Label>
                    <textarea
                      className="form-control form-control-sm"
                      rows={1}
                      {...register("description", {
                        required: "Description is required",
                      })}
                      placeholder="Enter description"
                    />
                  </Form.Group>
                  {errors.description && (
                    <small className="text-danger">
                      {errors.description.message}
                    </small>
                  )}
                </Col>
              </Row>
            </div>
            <Row className="g-3 mb-3">
              <Col xs={6}>
                <Form.Group className="d-flex flex-column focus-z-index position-relative">
                  <Form.Label
                    className="fw-semibold mb-1"
                    style={{ fontSize: "13px", color: "#555" }}
                  >
                    Round Robin Type<span className="text-danger ms-1">*</span>
                  </Form.Label>
                  <Controller
                    name="type"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        styles={customStyles}
                        classNamePrefix="react-select"
                        options={roundRobinTypeOptions}
                        placeholder="Select type..."
                        isSearchable={false}
                        onChange={(val) => {
                          field.onChange(val);
                          // UI fix: reset specific fields on type change
                          if (val.value === "custom_algorithm") {
                            setValue("owners_multi", []);
                            if (fields.length === 0)
                              append({ owner: null, count: 1 });
                          }
                        }}
                      />
                    )}
                  />
                </Form.Group>
              </Col>
            </Row>
            {!isCustom && (
              <Row className="g-3 mb-3">
                <Col xs={12}>
                  <Form.Group className="d-flex flex-column focus-z-index position-relative">
                    <Form.Label
                      className="fw-semibold mb-1"
                      style={{ fontSize: "13px", color: "#555" }}
                    >
                      Lead Owners<span className="text-danger ms-1">*</span>
                    </Form.Label>
                    <Controller
                      name="owners_multi"
                      control={control}
                      rules={{ required: "Select at least one lead owner" }}
                      render={({ field }) => (
                        <Select
                          {...field}
                          isMulti
                          options={leadOwnerOptions}
                          classNamePrefix="react-select"
                          styles={customStyles}
                          placeholder="Select lead owners..."
                          closeMenuOnSelect={false}
                        />
                      )}
                    />
                    {errors.owners_multi && (
                      <Form.Text className="text-danger">
                        {errors.owners_multi.message}
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>
              </Row>
            )}
            {isCustom && (
              <div className="mb-3">
                {fields.map((field, index) => {
                  const selectedOwnerValues =
                    watch("custom_owners")
                      ?.filter((_, i) => i !== index)
                      ?.map((row) => row?.owner?.value)
                      ?.filter(Boolean) || [];
                      const filteredOwnerOptions = leadOwnerOptions.filter(
                      (opt) => !selectedOwnerValues.includes(opt.value),
                  );
                  return (
                    <Row key={field.id} className="g-2 mb-2 align-items-end">
                      <Col xs={6}>
                        <Form.Group className="d-flex flex-column focus-z-index position-relative">
                          <Form.Label
                            className="fw-semibold mb-1"
                            style={{ fontSize: "13px", color: "#555" }}
                          >
                            Lead Owner
                            <span className="text-danger ms-1">*</span>
                          </Form.Label>
                          <Controller
                            name={`custom_owners.${index}.owner`}
                            control={control}
                            rules={{ required: "Select Owner" }}
                            render={({ field: f }) => (
                              <Select
                                {...f}
                                options={filteredOwnerOptions}
                                styles={customStyles}
                                classNamePrefix="react-select"
                                placeholder="Select owner..."
                              />
                            )}
                          />
                        </Form.Group>
                      </Col>
                      <Col xs={5}>
                        <Form.Group className="d-flex flex-column focus-z-index position-relative">
                          <Form.Label
                            className="fw-semibold mb-1"
                            style={{ fontSize: "13px", color: "#555" }}
                          >
                            Count<span className="text-danger ms-1">*</span>
                          </Form.Label>
                          <Form.Control
                            type="number"
                            min="1"
                            {...register(`custom_owners.${index}.count`, {
                              required: "Count is required",
                              min: {
                                value: 1,
                                message: "Count 1 se kam nahi ho sakta",
                              },
                              valueAsNumber: true,
                            })}
                            onKeyDown={(e) => {
                              if (e.key === "-" || e.key === "e") {
                                e.preventDefault();
                              }
                            }}
                            placeholder="Count"
                            style={{
                              height: "38px",
                              fontSize: "14px",
                              color: "#0791ff",
                              fontWeight: 600,
                              borderRadius: "6px",
                            }}
                          />
                        </Form.Group>
                      </Col>
                      <Col xs={1} className="d-flex align-items-end pb-2">
                        {fields.length > 1 && (
                          <button
                            type="button"
                            onClick={() => remove(index)}
                            style={{
                              width: "34px",
                              height: "34px",
                              border: "0.4px solid red",
                              borderRadius: "50px",
                              background: "transparent",
                            }}
                          >
                            <IconTrash
                              stroke={2}
                              style={{
                                stroke: "red",
                                width: "16px",
                                height: "16px",
                              }}
                            />
                          </button>
                        )}
                      </Col>
                      {errors.custom_owners?.[index]?.owner && (
                        <small className="text-danger d-block">
                          plz select lead owner
                        </small>
                      )}
                    </Row>
                  );
                })}
                <div className="mt-2">
                  <button
                    type="button"
                    onClick={() => append({ owner: null, count: 1 })}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#0791ff",
                      fontSize: "13px",
                      fontWeight: 500,
                    }}
                  >
                    Add Owner +
                  </button>
                </div>
              </div>
            )}
          </div>
          <div
            style={{
              position: "fixed",
              width: "60%",
              bottom: 0,
              height: "74px",
              zIndex: 2,
              right: 0,
              background: "#fefefe",
              paddingRight: "20px",
              borderTop: "1px solid #c4ebf7",
            }}
          >
            <div className="d-flex justify-content-end gap-2 pt-4">
              <Button
                type="button"
                onClick={handleClose}
                className="btn-default-outline"
                variant="none"
                style={{ width: "90px" }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="default"
                style={{ width: "90px" }}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Saving..." : editedId ? "Update" : "Save"}
              </Button>
            </div>
          </div>
        </form>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default CreateRoundRobinOffcanvas;
